import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { KpiTile, DataTable, type DataTableColumn, type SortState } from '../../patterns';
import { useFieldReview } from './useFieldReview';
import { BlockDetailPanel } from './BlockDetailPanel';
import type { BlockReview } from '../../../scouting/reviewModel';
import { naturalCompare } from '../../../utils/naturalCompare';
import './review.css';

function deltaText(t: (k: string, o?: Record<string, unknown>) => string, n: number, higherIsBetter = false): React.ReactNode {
  if (n === 0) return <span className="rev-delta">— {t('fieldScout.review.summary.vsLastWeek')}</span>;
  const up = n > 0;
  const good = higherIsBetter ? up : !up;   // arrow shows direction; colour shows whether it's good
  return <span className={`rev-delta ${good ? 'rev-delta--good' : 'rev-delta--bad'}`}>{up ? '▲' : '▼'} {Math.abs(n)} {t('fieldScout.review.summary.vsLastWeek')}</span>;
}

async function downloadPestReport(orchardId?: string) {
  const token = localStorage.getItem('token') || '';
  const params = new URLSearchParams({ includeLogo: 'true' });
  if (orchardId) params.append('orchards', orchardId);
  const res = await axios.get(`/api/v1/reports/pest-summary-pdf?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `Pest_Summary_${new Date().toISOString().split('T')[0]}.pdf`);
  document.body.appendChild(link);
  link.click();
  if (link.parentNode) link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
}

const FlagCounts: React.FC<{ b: BlockReview }> = ({ b }) => (
  <span className="rev-mini">
    <span className="rev-mini__ex">{b.counts.exceeded}</span>
    <span className="rev-mini__ap">{b.counts.approaching}</span>
    <span className="rev-mini__bl">{b.counts.below}</span>
  </span>
);

const ReviewSummaryPage: React.FC = () => {
  const { t } = useTranslation();
  const { loading, error, current, kpis, deltas, hasPrevious } = useFieldReview();
  const [params] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState | null>({ key: 'blockId', direction: 'asc' });

  // preselect from ?block= or first row
  useEffect(() => {
    if (!current.length) return;
    const wanted = params.get('block');
    const found = wanted && current.find(b => b.blockId === wanted);
    setSelectedId(prev => prev ?? (found ? found.blockId : current[0].blockId));
  }, [current, params]);

  const sortedRows = useMemo(() => {
    const rows = [...current];
    if (!sort) return rows;
    const dir = sort.direction === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      // Ties fall back to natural block order (not the backend's date order).
      switch (sort.key) {
        case 'exceeded': return (a.counts.exceeded - b.counts.exceeded) * dir || naturalCompare(a.blockId, b.blockId);
        case 'coverage': return (a.coveragePct - b.coveragePct) * dir || naturalCompare(a.blockId, b.blockId);
        case 'worst': return (a.worstRatio - b.worstRatio) * dir || naturalCompare(a.blockId, b.blockId);
        default: return naturalCompare(a.blockId, b.blockId) * dir;
      }
    });
    return rows;
  }, [current, sort]);

  const selected = current.find(b => b.blockId === selectedId) || null;

  const worstPill = (b: BlockReview) => b.worst
    ? <span className={`rev-pill rev-pill--${b.worst.flag}`}>{b.worst.code} {b.worst.display}</span>
    : <span className="rev-pill rev-pill--below">{t('fieldScout.review.summary.healthy')}</span>;

  const renderMobileCard = (b: BlockReview): React.ReactNode => (
    <div className="rev-card">
      <div className="rev-card__top">
        <span><b>{b.blockId}</b> <span className="text-muted">{b.variety}</span></span>
        <span className="rev-card__cov">{b.coveragePct}%</span>
      </div>
      <div className="rev-card__bottom">
        <FlagCounts b={b} />
        {worstPill(b)}
      </div>
    </div>
  );

  const columns: DataTableColumn<BlockReview>[] = [
    { key: 'blockId', label: t('fieldScout.review.summary.colBlock'), sortable: true, render: b => <b>{b.blockId}</b> },
    { key: 'variety', label: t('fieldScout.review.summary.colVariety'), render: b => b.variety || '—' },
    { key: 'coverage', label: t('fieldScout.review.summary.colCoverage'), sortable: true, render: b => `${b.coveragePct}%` },
    { key: 'exceeded', label: t('fieldScout.review.summary.colFlags'), sortable: true, render: b => <FlagCounts b={b} /> },
    { key: 'worst', label: t('fieldScout.review.summary.colWorst'), render: worstPill },
    { key: 'open', label: '', render: b => <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setSelectedId(b.blockId)}>{t('fieldScout.review.summary.open')}</button> },
  ];

  if (error) return <div className="alert alert-danger m-3" role="alert">{error}</div>;
  if (loading) return <div className="p-4 text-muted">{t('fieldScout.review.summary.title')}…</div>;

  return (
    <div className="rev-page">
      <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
        <div>
          <h1 className="h4 mb-0">{t('fieldScout.review.summary.title')}</h1>
          <div className="text-muted small">{t('fieldScout.review.summary.sub', { count: current.length })}</div>
        </div>
        <div className="ms-auto d-flex gap-2">
          <Link className="btn btn-outline-secondary btn-sm" to="/scouting/review/matrix">{t('fieldScout.review.summary.openMatrix')} →</Link>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { downloadPestReport().catch(e => console.error('[ReviewSummaryPage] PDF error:', e)); }}>⤓ {t('fieldScout.review.summary.pestReportPdf')}</button>
        </div>
      </div>

      <div className="rev-kpis">
        <KpiTile primary label={t('fieldScout.review.summary.kpiBlocksOver')} value={<>{kpis.blocksOverThreshold} <span className="text-muted fs-6">/ {kpis.totalBlocks}</span></>} delta={hasPrevious ? deltaText(t, deltas.blocksOverThreshold) : undefined} />
        <KpiTile label={t('fieldScout.review.summary.kpiExceedances')} value={kpis.pestBlockExceedances} delta={hasPrevious ? deltaText(t, deltas.pestBlockExceedances) : undefined} />
        <KpiTile label={t('fieldScout.review.summary.kpiApproaching')} value={kpis.approaching} delta={hasPrevious ? deltaText(t, deltas.approaching) : undefined} />
        <KpiTile label={t('fieldScout.review.summary.kpiAvgCoverage')} value={`${kpis.avgCoveragePct}%`} delta={hasPrevious ? deltaText(t, deltas.avgCoveragePct, true) : undefined} />
      </div>

      {current.length === 0 ? (
        <div className="card card-pad text-muted">{t('fieldScout.review.summary.empty')}</div>
      ) : (
        <div className="rev-grid">
          <div className="card">
            <DataTable<BlockReview>
              columns={columns} rows={sortedRows} rowKey={b => b.blockId}
              onRowClick={b => setSelectedId(b.blockId)}
              sort={sort} onSortChange={setSort}
              mobileCardRow={renderMobileCard}
              rowClassName={b => (b.blockId === selectedId ? 'table-active' : '')}
            />
          </div>
          {selected && <BlockDetailPanel block={selected} onGenerateReport={b => { downloadPestReport(b.orchardId || undefined).catch(e => console.error('[ReviewSummaryPage] PDF error:', e)); }} />}
        </div>
      )}
    </div>
  );
};

export default ReviewSummaryPage;
